import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Check, CreditCard, Loader2, Sparkles } from "lucide-react";
import { fetchProducts, type ShopifyProduct } from "@/lib/shopify";
import { Button } from "@/components/ui/button";
import { useCartStore } from "@/stores/cartStore";
import { toast } from "sonner";
import { TIERED_PACK_PRICING } from "@/routes/products.$handle";
import { computeCartDiscounts } from "@/lib/bundleDiscount";
import { NO_COST_EMI_MIN_INR } from "@/lib/checkout-pricing";
import { createEventId, trackGenerateLead } from "@/lib/analytics/ecommerce";
import { saveQuizLead } from "@/lib/quiz-lead.functions";
import { saveMyQuizResult } from "@/lib/quiz-result.functions";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";

import { getAnonymousId } from "@/lib/identity/anonymousId";
import { track as ciTrack } from "@/lib/customer-intelligence/tracker";

import { useT } from "@/lib/i18n/provider";
import { useSkuRoutineCopy, useProductTagline } from "@/lib/i18n/content";
import {
  recommendProtocol,
  SKU_META,
  type PrimaryGoal,
  type SKU,
  type Symptom,
} from "@/lib/quizRouter";
import heroMag__asset from "@/assets/product-hero/mag-absorb-hero.png.asset.json";
const heroMag = heroMag__asset.url;
import heroOva__asset from "@/assets/product-hero/ova-balance-hero.png.asset.json";
const heroOva = heroOva__asset.url;
import heroLiver__asset from "@/assets/liver-biome-v2/01-hero.webp.asset.json";
import { PushOptInCard } from "@/components/notifications/PushOptInCard";
import { ConsentBlock, defaultConsentSelection, useConsentRecorder, type ConsentSelection } from "@/components/consent/ConsentBlock";
const heroLiver = heroLiver__asset.url;

const HERO_BY_SKU: Record<SKU, string> = {
  "mag-absorb-pro": heroMag,
  "liver-biome-plus": heroLiver,
  "ova-balance": heroOva,
};

export const Route = createFileRoute("/quiz")({
  component: QuizPage,
  head: () => ({
    meta: [
      { title: "Find your protocol, 2-minute TruWe quiz" },
      {
        name: "description",
        content:
          "Answer 5 quick questions and get a personalised TruWe protocol, clinically-studied actives, science-backed routing, ready to add to cart.",
      },
      { property: "og:title", content: "Find your protocol, 2-minute TruWe quiz" },
      {
        property: "og:description",
        content:
          "Five quick questions. A personalised TruWe protocol grounded in clinical research.",
      },
      { property: "og:url", content: "https://truwe.in/quiz" },
    ],
    links: [{ rel: "canonical", href: "https://truwe.in/quiz" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Find your protocol. TruWe quiz",
          url: "https://truwe.in/quiz",
          description: "Personalised TruWe protocol from 5 quick questions.",
          isPartOf: { "@id": "https://truwe.in/#website" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://truwe.in/" },
            { "@type": "ListItem", position: 2, name: "Quiz", item: "https://truwe.in/quiz" },
          ],
        }),
      },
    ],
  }),
});

type Duration = "<1mo" | "1-3mo" | "3-6mo" | "6mo+";
type SupplementState = "none" | "some";

type Answers = {
  primary: PrimaryGoal | null;
  symptoms: Symptom[];
  duration: Duration | null;
  supplements: SupplementState | null;
  notes: string;
};

const PRIMARY_OPTIONS: { id: PrimaryGoal; labelKey: string; subKey: string }[] = [
  { id: "energy", labelKey: "qz.goal.energy", subKey: "qz.goal.energy.sub" },
  { id: "hormones", labelKey: "qz.goal.hormones", subKey: "qz.goal.hormones.sub" },
  { id: "liver", labelKey: "qz.goal.liver", subKey: "qz.goal.liver.sub" },
  { id: "sleep", labelKey: "qz.goal.sleep", subKey: "qz.goal.sleep.sub" },
  { id: "multiple", labelKey: "qz.goal.multiple", subKey: "qz.goal.multiple.sub" },
];

const SYMPTOM_OPTIONS: { id: Symptom; labelKey: string }[] = [
  { id: "fatigue", labelKey: "qz.sym.fatigue" },
  { id: "poorSleep", labelKey: "qz.sym.poorSleep" },
  { id: "hormonalAcne", labelKey: "qz.sym.hormonalAcne" },
  { id: "bloating", labelKey: "qz.sym.bloating" },
  { id: "fogMood", labelKey: "qz.sym.fogMood" },
  { id: "crampsRecovery", labelKey: "qz.sym.crampsRecovery" },
];

const DURATION_OPTIONS: { id: Duration; labelKey: string }[] = [
  { id: "<1mo", labelKey: "qz.dur.lt1mo" },
  { id: "1-3mo", labelKey: "qz.dur.1to3mo" },
  { id: "3-6mo", labelKey: "qz.dur.3to6mo" },
  { id: "6mo+", labelKey: "qz.dur.6moPlus" },
];

const TOTAL_STEPS = 5; // Q1, Q2, Q3, Q4, result

function QuizPage() {
  const t = useT();
  const { data = [] } = useQuery({
    queryKey: ["products"],
    queryFn: () => fetchProducts(24),
  });
  const products = data.map((p) => p.node);

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({
    primary: null,
    symptoms: [],
    duration: null,
    supplements: null,
    notes: "",
  });

  // Q3 (duration) is skipped when goal is "multiple" — per spec.
  const skipDuration = answers.primary === "multiple";

  // Funnel: one "quiz_start" per visit to the questionnaire.
  useEffect(() => {
    ciTrack("quiz_start", {});
  }, []);

  // Scroll back to the top whenever the step changes so the new question is
  // visible immediately (especially important on mobile).
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  function next() {
    setStep((s) => {
      if (s === 1 && skipDuration) return 3;
      return s + 1;
    });
  }
  function back() {
    setStep((s) => {
      if (s === 3 && skipDuration) return 1;
      return Math.max(0, s - 1);
    });
  }

  const canProceed =
    (step === 0 && answers.primary !== null) ||
    (step === 1 && answers.symptoms.length > 0) ||
    (step === 2 && answers.duration !== null) ||
    (step === 3 && answers.supplements !== null);

  // Visible progress accounting for skipped Q3.
  const visibleStep = skipDuration && step > 2 ? step - 1 : step;
  const visibleTotal = skipDuration ? TOTAL_STEPS - 1 : TOTAL_STEPS;
  const progressPct = Math.min(visibleStep / visibleTotal, 1) * 100;

  return (
    <div className="bg-brand-soft/30">
      <div className="mx-auto max-w-2xl px-4 py-10 md:py-16">
        <div className="mb-7">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-brand"
          >
            <Sparkles className="h-3 w-3" /> {t("qz.finder")}
          </Link>
          <div className="mt-3 flex h-1.5 overflow-hidden rounded-full bg-white">
            <div
              className="h-full bg-brand transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          {step < TOTAL_STEPS - 1 && (
            <p className="mt-1.5 text-[11px] text-muted-foreground tabular-nums">
              {t("qz.step", { n: visibleStep + 1, total: visibleTotal })}
            </p>
          )}
        </div>

        {step === 0 && (
          <QuestionShell
            eyebrow={t("qz.q1.eyebrow")}
            title={t("qz.q1.title")}
            subtitle={t("qz.q1.sub")}
          >
            <div className="space-y-2.5">
              {PRIMARY_OPTIONS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setAnswers((a) => ({ ...a, primary: o.id }))}
                  className={`group flex w-full items-start justify-between gap-3 rounded-2xl border bg-white px-5 py-4 text-left transition ${
                    answers.primary === o.id
                      ? "border-brand ring-2 ring-brand/30 shadow-[0_8px_30px_-12px_rgba(30,94,255,0.4)]"
                      : "border-border hover:border-brand/40"
                  }`}
                  aria-pressed={answers.primary === o.id}
                >
                  <div className="min-w-0">
                    <p className="font-display text-[15px] font-semibold text-brand-deep">
                      {t(o.labelKey as never)}
                    </p>
                    <p className="mt-0.5 text-[12.5px] text-muted-foreground">
                      {t(o.subKey as never)}
                    </p>
                  </div>
                  <span
                    className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition ${
                      answers.primary === o.id ? "border-brand bg-brand" : "border-border"
                    }`}
                  >
                    {answers.primary === o.id && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                  </span>
                </button>
              ))}
            </div>
          </QuestionShell>
        )}

        {step === 1 && (
          <QuestionShell
            eyebrow={t("qz.q2.eyebrow")}
            title={t("qz.q2.title")}
            subtitle={t("qz.q2.sub")}
          >
            <div className="flex flex-wrap gap-2">
              {SYMPTOM_OPTIONS.map((o) => {
                const on = answers.symptoms.includes(o.id);
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() =>
                      setAnswers((a) => ({
                        ...a,
                        symptoms: on
                          ? a.symptoms.filter((x) => x !== o.id)
                          : [...a.symptoms, o.id],
                      }))
                    }
                    className={`rounded-full border px-4 py-2.5 text-[13px] font-medium transition ${
                      on
                        ? "border-brand bg-brand text-white shadow-[0_6px_20px_-8px_rgba(30,94,255,0.5)]"
                        : "border-border bg-white text-brand-deep hover:border-brand/50"
                    }`}
                    aria-pressed={on}
                  >
                    {on && <Check className="mr-1 inline h-3 w-3" />}
                    {t(o.labelKey as never)}
                  </button>
                );
              })}
            </div>
          </QuestionShell>
        )}

        {step === 2 && !skipDuration && (
          <QuestionShell
            eyebrow={t("qz.q3.eyebrow")}
            title={t("qz.q3.title")}
            subtitle={t("qz.q3.sub")}
          >
            <div className="grid grid-cols-2 gap-2.5">
              {DURATION_OPTIONS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setAnswers((a) => ({ ...a, duration: o.id }))}
                  className={`rounded-2xl border bg-white px-4 py-4 text-left text-[13px] font-semibold transition ${
                    answers.duration === o.id
                      ? "border-brand ring-2 ring-brand/30 text-brand-deep"
                      : "border-border text-foreground hover:border-brand/50"
                  }`}
                >
                  {t(o.labelKey as never)}
                </button>
              ))}
            </div>
          </QuestionShell>
        )}

        {step === 3 && (
          <QuestionShell
            eyebrow={skipDuration ? t("qz.q3.eyebrow") : t("qz.q4.eyebrow")}
            title={t("qz.q4.title")}
            subtitle={t("qz.q4.sub")}
          >
            <div className="grid grid-cols-2 gap-3">
              <YesNo
                active={answers.supplements === "none"}
                onClick={() => setAnswers((a) => ({ ...a, supplements: "none" }))}
              >
                {t("qz.supp.none")}
              </YesNo>
              <YesNo
                active={answers.supplements === "some"}
                onClick={() => setAnswers((a) => ({ ...a, supplements: "some" }))}
              >
                {t("qz.supp.some")}
              </YesNo>
            </div>
            {answers.supplements === "some" && (
              <div className="mt-4 animate-fade-in">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-brand-deep">
                  {t("qz.supp.label")}
                </label>
                <textarea
                  value={answers.notes}
                  onChange={(e) => setAnswers((a) => ({ ...a, notes: e.target.value }))}
                  placeholder={t("qz.supp.placeholder")}
                  rows={3}
                  maxLength={500}
                  className="mt-2 w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
                <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">
                  {t("qz.supp.note")}
                </p>
              </div>
            )}
          </QuestionShell>
        )}

        {step >= TOTAL_STEPS - 1 && answers.primary && (
          <ResultStep answers={answers} products={products} onRestart={() => {
            setStep(0);
            setAnswers({
              primary: null,
              symptoms: [],
              duration: null,
              supplements: null,
              notes: "",
            });
          }} />
        )}

        {step < TOTAL_STEPS - 1 && (
          <div className="mt-10 flex items-center justify-between">
            <Button variant="ghost" onClick={back} disabled={step === 0}>
              <ArrowLeft className="mr-1 h-4 w-4" /> {t("qz.back")}
            </Button>
            <Button
              size="lg"
              className="rounded-full bg-brand px-7 hover:bg-brand/90"
              disabled={!canProceed}
              onClick={next}
            >
              {step === 3 ? t("qz.seeProtocol") : t("qz.continue")}
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function QuestionShell({
  eyebrow,
  title,
  subtitle,
  children,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="animate-fade-in">
      <p className="text-[10.5px] font-bold uppercase tracking-[0.2em] text-brand">{eyebrow}</p>
      <h1 className="mt-2 font-display text-2xl font-bold leading-tight text-brand-deep md:text-3xl">
        {title}
      </h1>
      {subtitle && <p className="mt-2 text-[14px] text-muted-foreground">{subtitle}</p>}
      <div className="mt-7">{children}</div>
    </div>
  );
}

function YesNo({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-2xl border bg-white px-4 py-4 text-[13px] font-semibold transition ${
        active
          ? "border-brand ring-2 ring-brand/30 text-brand-deep"
          : "border-border text-foreground hover:border-brand/50"
      }`}
    >
      {children}
    </button>
  );
}

function ResultStep({
  answers,
  products,
  onRestart,
}: {
  answers: Answers;
  products: ShopifyProduct["node"][];
  onRestart: () => void;
}) {
  const t = useT();
  const skuCopy = useSkuRoutineCopy();
  const skuTagline = useProductTagline();
  const [showing, setShowing] = useState<"loading" | "result">("loading");
  const addItems = useCartStore((s) => s.addItems);
  const isLoading = useCartStore((s) => s.isLoading);
  const [added, setAdded] = useState(false);
  /**
   * "I have multiple concerns" gets the full 3-product TruWe Protocol
   * pre-selected, so the 15% trio bundle is the default path for them.
   */
  const wantsFullProtocol = answers.primary === "multiple";
  const [includeOthers, setIncludeOthers] = useState<SKU[]>(() =>
    wantsFullProtocol ? (["mag-absorb-pro", "liver-biome-plus", "ova-balance"] as SKU[]) : [],
  );
  /** 30 / 60 / 90-day pack ladder for the primary product. 60-day pre-selected. */
  const [packQty, setPackQty] = useState<1 | 2 | 3>(2);
  const [lead, setLead] = useState({ name: "", email: "", phone: "", consent: true });
  const [consentChannels, setConsentChannels] = useState<ConsentSelection>(() => defaultConsentSelection(["email", "whatsapp"]));
  const recordConsent = useConsentRecorder("quiz_results");
  const [leadState, setLeadState] = useState<"idle" | "saving" | "saved">("idle");
  const [leadError, setLeadError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setShowing("result"), 1400);
    return () => clearTimeout(t);
  }, []);

  // Funnel: the visitor answered every question and reached the protocol.
  useEffect(() => {
    ciTrack("quiz_complete", {
      primary: answers.primary,
      symptomCount: answers.symptoms.length,
      duration: answers.duration,
      supplements: answers.supplements,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const rec = useMemo(
    () => (answers.primary ? recommendProtocol(answers.primary, answers.symptoms) : null),
    [answers],
  );

  // Signed-in visitors get the result stored on their account automatically —
  // no form, no extra tap. Anonymous visitors keep the email-capture path.
  const persistResult = useServerFn(saveMyQuizResult);
  const [savedToAccount, setSavedToAccount] = useState(false);
  useEffect(() => {
    if (!rec) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.auth.getUser();
        if (!data.user || cancelled) return;
        const res = await persistResult({
          data: {
            primaryGoal: answers.primary,
            symptoms: answers.symptoms,
            duration: answers.duration,
            supplements: answers.supplements,
            recommendedSku: rec.primary,
            supportingSku: rec.supporting,
            bundle: rec.bundle,
          },
        });
        if (!cancelled && res?.ok) setSavedToAccount(true);
      } catch {
        // Saving is a convenience — never block the result screen.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rec?.primary, rec?.supporting]);



  // Match Shopify products to SKU handles.
  const productBySku = useMemo<Partial<Record<SKU, ShopifyProduct["node"]>>>(() => {
    const map: Partial<Record<SKU, ShopifyProduct["node"]>> = {};
    for (const p of products) {
      const h = p.handle.toLowerCase();
      if (h.includes("mag-absorb")) map["mag-absorb-pro"] = p;
      else if (h.includes("liver-biome")) map["liver-biome-plus"] = p;
      else if (h.includes("ova")) map["ova-balance"] = p;
    }
    return map;
  }, [products]);

  if (showing === "loading" || !rec) {
    return (
      <div className="flex flex-col items-center py-16 text-center animate-fade-in">
        <div className="relative">
          <div className="h-14 w-14 rounded-full border-2 border-brand-soft" />
          <div className="absolute inset-0 h-14 w-14 animate-spin rounded-full border-2 border-transparent border-t-brand" />
        </div>
        <p className="mt-5 font-display text-lg font-semibold text-brand-deep">
          {t("qz.loading.title")}
        </p>
        <p className="mt-1.5 text-[13px] text-muted-foreground">
          {t("qz.loading.sub")}
        </p>
      </div>
    );
  }

  const selectedSkus: SKU[] = Array.from(
    new Set<SKU>([
      rec.primary,
      ...(rec.supporting ? [rec.supporting] : []),
      ...includeOthers,
    ]),
  );
  const cartLines = selectedSkus
    .map((sku) => {
      const p = productBySku[sku];
      const v = p?.variants.edges[0]?.node;
      if (!p || !v) return null;
      // The primary product follows the selected 30/60/90-day pack ladder;
      // supporting products go in as a single 30-day pack.
      const quantity = sku === rec.primary ? packQty : 1;
      return {
        sku,
        variantId: v.id,
        productHandle: p.handle,
        productTitle: p.title,
        imageUrl: p.images.edges[0]?.node.url ?? null,
        price: v.price,
        quantity,
      };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

  // Price the exact selected lines through the SAME engine the cart and
  // checkout use (campaign → bundle → pack ladder), so the quiz can never
  // quote a total the cart won't honour.
  const quizPricing = computeCartDiscounts(
    cartLines.map((l) => ({
      key: l.variantId,
      handle: l.productHandle,
      title: l.productTitle,
      unitPricePaise: Math.round(parseFloat(l.price.amount) * 100),
      quantity: l.quantity,
    })),
  );
  const total = cartLines.reduce(
    (s, l) => s + parseFloat(l.price.amount) * l.quantity,
    0,
  );
  const savings = Math.round(quizPricing.discountPaise / 100);
  const finalPrice = Math.max(0, Math.round(total) - savings);
  const supplyDays = packQty * 30;

  async function handleAdd() {
    if (cartLines.length === 0) {
      toast.error(t("qz.toast.loading"));
      return;
    }
    const url = await addItems(cartLines.map(({ sku: _sku, ...rest }) => rest));
    if (!url) {
      toast.error(t("qz.toast.addFailed"));
      return;
    }
    toast.success(t("qz.toast.added"));
    setAdded(true);
    (window as unknown as { __openCart?: () => void }).__openCart?.();
  }

  async function handleSaveLead(e: React.FormEvent) {
    e.preventDefault();
    if (leadState === "saving") return;
    const email = lead.email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setLeadError(t("qz.lead.invalidEmail"));
      return;
    }
    setLeadError(null);
    setLeadState("saving");
    const eventId = createEventId();
    try {
      const res = await saveQuizLead({
        data: {
          anonymousId: getAnonymousId(),
          name: lead.name.trim() || undefined,
          email,
          phone: lead.phone.trim() || undefined,
          consentMarketing: consentChannels.email === true || consentChannels.whatsapp === true,
          quizAnswers: {
            primary: answers.primary,
            symptoms: answers.symptoms,
            duration: answers.duration,
            supplements: answers.supplements,
          },
          recommendedSku: rec!.primary,
          supportingSku: rec!.supporting ?? undefined,
          selectedPackQty: packQty,
          valueInr: finalPrice,
          eventId,
        },
      });
      if (!res.ok) {
        setLeadError(t("qz.lead.saveFailed"));
        setLeadState("idle");
        return;
      }
      // Unticked boxes here must not clear an earlier opt-in.
      void recordConsent(consentChannels, { explicit: false });
      trackGenerateLead({
        recommendedSku: rec!.primary,
        quizPrimaryGoal: String(answers.primary ?? ""),
        value: finalPrice,
        eventId,
      });
      ciTrack("quiz_lead_saved", { recommendedSku: rec!.primary, packQty });
      setLeadState("saved");
      toast.success(t("qz.lead.sent"));
    } catch {
      setLeadError(t("qz.lead.saveFailed"));
      setLeadState("idle");
    }
  }


  const otherSkus: SKU[] = (["mag-absorb-pro", "liver-biome-plus", "ova-balance"] as SKU[]).filter(
    (s) => s !== rec.primary && s !== rec.supporting,
  );

  return (
    <div className="animate-fade-in">
      <p className="text-[10.5px] font-bold uppercase tracking-[0.2em] text-brand">
        {t("qz.result.eyebrow")}
      </p>
      <h1 className="mt-2 font-display text-3xl font-bold leading-tight text-brand-deep md:text-4xl">
        {t("qz.result.title", { days: supplyDays })}
      </h1>
      <p className="mt-2.5 text-[14px] text-muted-foreground">
        {t("qz.result.sub")}
      </p>

      {/* Primary recommendation */}
      <ProductRow sku={rec.primary} role={t("qz.role.primary")} product={productBySku[rec.primary]} />
      {rec.supporting && (
        <ProductRow
          sku={rec.supporting}
          role={t("qz.role.supporting")}
          product={productBySku[rec.supporting]}
        />
      )}

      {/* Multiple concerns → the full 3-product TruWe Protocol is the default */}
      {wantsFullProtocol && (
        <section className="mt-5 rounded-2xl border border-brand/25 bg-brand-soft/40 p-4">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.2em] text-brand">
            {t("qz.trio.eyebrow")}
          </p>
          <p className="mt-1.5 text-[13.5px] leading-snug text-brand-deep">
            You flagged multiple concerns, so we&apos;ve pre-selected all three formulas. Mag
            Absorb Pro, Liver Biome Plus and Ova Balance. They check out together with{" "}
            <strong>15% off</strong>, applied automatically in your cart.
          </p>
        </section>
      )}

      {/* Pack ladder, 30 / 60 / 90 day */}
      <section className="mt-6">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.2em] text-brand">
          {t("qz.packs.eyebrow")}
        </p>
        <p className="mt-1 text-[12.5px] text-muted-foreground">
          {t("qz.packs.sub")}
        </p>
        <div className="mt-3 grid gap-2.5 sm:grid-cols-3">
          {([1, 2, 3] as const).map((q) => {
            const tier = TIERED_PACK_PRICING[rec.primary]?.[q];
            if (!tier) return null;
            const on = packQty === q;
            return (
              <button
                key={q}
                type="button"
                onClick={() => setPackQty(q)}
                aria-pressed={on}
                className={`relative rounded-2xl border bg-white p-3.5 text-left transition ${
                  on ? "border-brand ring-1 ring-brand/30" : "border-border hover:border-brand/40"
                }`}
              >
                {q === 2 && (
                  <span className="absolute -top-2 right-3 rounded-full bg-brand px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-white">
                    {t("qz.packs.popular")}
                  </span>
                )}
                <p className="font-display text-[15px] font-bold text-brand-deep">
                  {t("qz.packs.day", { days: q * 30 })}
                </p>
                <p className="mt-0.5 text-[11.5px] text-muted-foreground">
                  {t("qz.packs.perDay", {
                    n: q,
                    unit: q === 1 ? t("qz.packs.pack") : t("qz.packs.packs"),
                    price: Math.round(tier.price / (q * 30)),
                  })}
                </p>
                <p className="mt-1.5 font-display text-[16px] font-bold tabular-nums text-brand-deep">
                  ₹{Math.round(tier.price).toLocaleString("en-IN")}
                  {tier.mrp > tier.price && (
                    <span className="ml-1.5 text-[12px] font-normal text-muted-foreground line-through">
                      ₹{Math.round(tier.mrp).toLocaleString("en-IN")}
                    </span>
                  )}
                </p>
              </button>
            );
          })}
        </div>
      </section>


      {/* Why this works */}
      <section className="mt-6 rounded-2xl border border-brand/15 bg-brand-soft/40 p-5">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.2em] text-brand">
          {t("quiz.whyThis")}
        </p>
        <p className="mt-2 text-[14px] leading-relaxed text-brand-deep">
          {skuCopy(rec.primary, SKU_META[rec.primary]).why}
        </p>
      </section>

      {/* Timeline */}
      <section className="mt-6">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.2em] text-brand">
          {t("quiz.whatToExpect")}
        </p>
        <div className="mt-3 grid gap-2.5 md:grid-cols-3">
          <TimelineCard day={t("quiz.day", { n: 7 })} text={skuCopy(rec.primary, SKU_META[rec.primary]).timeline.day7} />
          <TimelineCard day={t("quiz.day", { n: 15 })} text={skuCopy(rec.primary, SKU_META[rec.primary]).timeline.day15} />
          <TimelineCard day={t("quiz.day", { n: 30 })} text={skuCopy(rec.primary, SKU_META[rec.primary]).timeline.day30} />
        </div>
      </section>

      {/* Add protocol, sticky-feel CTA card */}
      <section className="mt-7 rounded-2xl border border-border bg-white p-4 shadow-[0_12px_40px_-20px_rgba(30,94,255,0.35)]">
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-[10.5px] font-bold uppercase tracking-[0.2em] text-brand-deep">
              {cartLines.length >= 3
                ? t("qz.cta.fullProtocol")
                : cartLines.length === 2
                  ? t("qz.cta.protocol")
                  : t("qz.cta.startingPoint")}
            </p>
            <p className="font-display text-2xl font-bold tabular-nums text-brand-deep">
              ₹{Math.round(finalPrice)}
              {savings > 0 && (
                <span className="ml-2 text-sm font-normal text-muted-foreground line-through">
                  ₹{Math.round(total)}
                </span>
              )}
            </p>
          </div>
          {savings > 0 && (
            <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-200">
              {t("qz.cta.save", { amount: Math.round(savings) })}
            </span>
          )}
        </div>
        {savings > 0 && (
          <p className="mt-1 text-[11.5px] text-muted-foreground">
            Includes ₹{Math.round(savings)} off, applied automatically in your cart.
          </p>
        )}
        {finalPrice >= NO_COST_EMI_MIN_INR && (
          <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-brand-soft/70 px-2.5 py-1 text-[11px] font-semibold text-brand-deep ring-1 ring-brand/20">
            <CreditCard className="h-3 w-3" /> {t("qz.cta.emi")}
          </p>
        )}

        <Button
          size="lg"
          disabled={isLoading || cartLines.length === 0}
          onClick={handleAdd}
          className="mt-3 h-12 w-full rounded-full bg-brand text-sm font-semibold text-white transition hover:bg-brand/90 active:scale-[0.98]"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : added ? (
            <span className="inline-flex items-center gap-1.5">
              <Check className="h-4 w-4" /> {t("qz.cta.added")}
            </span>
          ) : (
            cartLines.length >= 3
              ? t("qz.cta.addFull")
              : cartLines.length === 2
                ? t("qz.cta.addProtocol")
                : t("qz.cta.addToCart")
          )}
        </Button>
      </section>

      <PushOptInCard
        source="quiz_results"
        className="mt-5"
        title={t("qz.push.title")}
        description={t("qz.push.desc")}
      />

      {/* Contact capture, email me this protocol */}
      <section className="mt-5 rounded-2xl border border-brand/20 bg-brand-soft/30 p-4">
        {leadState === "saved" || savedToAccount ? (
          <div className="flex items-start gap-2.5">
            <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand text-white">
              <Check className="h-3.5 w-3.5" strokeWidth={3} />
            </span>
            <div>
              <p className="font-display text-[15px] font-bold text-brand-deep">
                {t("qz.lead.savedTitle")}
              </p>
              <p className="mt-0.5 text-[12.5px] text-muted-foreground">
                {savedToAccount ? (
                  <>
                    {t("qz.lead.savedAccount")}{" "}
                    <Link to="/account" className="font-medium text-brand underline">
                      {t("qz.lead.savedAccountLink")}
                    </Link>
                    .
                  </>
                ) : (
                  <>{t("qz.lead.savedEmail")}</>
                )}
              </p>
            </div>
          </div>

        ) : (
          <form onSubmit={handleSaveLead} noValidate>
            <p className="font-display text-[15px] font-bold text-brand-deep">
              {t("qz.lead.title")}
            </p>
            <p className="mt-0.5 text-[12.5px] text-muted-foreground">
              {t("qz.lead.sub")}
            </p>
            <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
              <div>
                <label htmlFor="quiz-lead-name" className="sr-only">
                  {t("qz.lead.name")}
                </label>
                <input
                  id="quiz-lead-name"
                  type="text"
                  autoComplete="name"
                  maxLength={100}
                  value={lead.name}
                  onChange={(e) => setLead((l) => ({ ...l, name: e.target.value }))}
                  placeholder={t("qz.lead.namePlaceholder")}
                  className="h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14px] text-brand-deep outline-none transition placeholder:text-muted-foreground focus:border-brand focus:ring-1 focus:ring-brand/30"
                />
              </div>
              <div>
                <label htmlFor="quiz-lead-email" className="sr-only">
                  {t("qz.lead.email")}
                </label>
                <input
                  id="quiz-lead-email"
                  type="email"
                  required
                  autoComplete="email"
                  maxLength={255}
                  value={lead.email}
                  onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))}
                  placeholder={t("qz.lead.email")}
                  className="h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14px] text-brand-deep outline-none transition placeholder:text-muted-foreground focus:border-brand focus:ring-1 focus:ring-brand/30"
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="quiz-lead-phone" className="sr-only">
                  {t("qz.lead.phone")}
                </label>
                <input
                  id="quiz-lead-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  maxLength={20}
                  value={lead.phone}
                  onChange={(e) => setLead((l) => ({ ...l, phone: e.target.value }))}
                  placeholder={t("qz.lead.phonePlaceholder")}
                  className="h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14px] text-brand-deep outline-none transition placeholder:text-muted-foreground focus:border-brand focus:ring-1 focus:ring-brand/30"
                />
              </div>
            </div>
            <ConsentBlock
              className="mt-3"
              value={consentChannels}
              onChange={setConsentChannels}
              channels={["email", "whatsapp"]}
            />
            {leadError && (
              <p className="mt-2 text-[12px] font-medium text-destructive" role="alert">
                {leadError}
              </p>
            )}
            <Button
              type="submit"
              size="lg"
              variant="outline"
              disabled={leadState === "saving"}
              className="mt-3 h-11 w-full rounded-full border-brand/40 text-sm font-semibold text-brand-deep"
            >
              {leadState === "saving" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t("qz.lead.submit")
              )}
            </Button>
          </form>
        )}
      </section>


      {/* Optional add-ons */}
      {otherSkus.length > 0 && (
        <section className="mt-7">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.2em] text-brand-deep">
            {t("qz.addons.title")}
          </p>
          <p className="mt-1 text-[12.5px] text-muted-foreground">
            {t("qz.addons.sub")}
          </p>
          <div className="mt-3 space-y-2">
            {otherSkus.map((sku) => {
              const p = productBySku[sku];
              const on = includeOthers.includes(sku);
              return (
                <button
                  key={sku}
                  type="button"
                  onClick={() =>
                    setIncludeOthers((cur) =>
                      cur.includes(sku) ? cur.filter((x) => x !== sku) : [...cur, sku],
                    )
                  }
                  className={`flex w-full items-center justify-between gap-3 rounded-2xl border bg-white px-4 py-3 text-left transition ${
                    on ? "border-brand ring-1 ring-brand/30" : "border-border hover:border-brand/40"
                  }`}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <img
                      src={HERO_BY_SKU[sku]}
                      alt={SKU_META[sku].name}
                      className="h-11 w-11 flex-shrink-0 rounded-lg bg-brand-soft object-cover"
                      loading="lazy"
                    />
                    <div className="min-w-0">
                      <p className="truncate font-display text-[14px] font-semibold text-brand-deep">
                        {SKU_META[sku].name}
                      </p>
                      <p className="truncate text-[11.5px] text-muted-foreground">
                        {skuTagline(sku, SKU_META[sku].tagline)}
                      </p>
                    </div>
                  </div>
                  <span className="flex items-center gap-2 text-[12px] font-semibold text-brand-deep tabular-nums">
                    {p && `₹${Math.round(parseFloat(p.priceRange.minVariantPrice.amount))}`}
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                        on ? "border-brand bg-brand" : "border-border"
                      }`}
                    >
                      {on && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <button
        type="button"
        onClick={onRestart}
        className="mt-8 text-[12px] font-medium text-muted-foreground underline-offset-2 hover:underline"
      >
        {t("qz.retake")}
      </button>
    </div>
  );
}

function ProductRow({
  sku,
  role,
  product,
}: {
  sku: SKU;
  role: string;
  product?: ShopifyProduct["node"];
}) {
  const skuTagline = useProductTagline();
  return (
    <Link
      to="/products/$handle"
      params={{ handle: sku }}
      className="mt-5 block overflow-hidden rounded-2xl border border-border bg-white transition hover:border-brand/40 hover:shadow-md"
    >
      <div className="flex items-stretch">
        <img
          src={HERO_BY_SKU[sku]}
          alt={SKU_META[sku].name}
          className="h-24 w-24 flex-shrink-0 bg-brand-soft object-cover md:h-28 md:w-28"
          loading="lazy"
        />
        <div className="min-w-0 flex-1 px-4 py-3">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand">{role}</p>
          <p className="mt-0.5 truncate font-display text-base font-bold text-brand-deep md:text-lg">
            {SKU_META[sku].name}
          </p>
          <p className="mt-0.5 text-[12.5px] leading-snug text-muted-foreground">
            {skuTagline(sku, SKU_META[sku].tagline)}
          </p>
          {product && (
            <p className="mt-1.5 font-display text-[15px] font-bold text-brand-deep tabular-nums">
              ₹{Math.round(parseFloat(product.priceRange.minVariantPrice.amount))}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}

function TimelineCard({ day, text }: { day: string; text: string }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-3.5">
      <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-brand">{day}</p>
      <p className="mt-1.5 text-[13px] leading-snug text-brand-deep">{text}</p>
    </div>
  );
}
